import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Grid, Typography, Switch, FormControlLabel, Chip, Alert, Button, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Card,
  CardActionArea, CardContent, Divider, MenuItem
} from "@mui/material";
import {
  Refresh, PhoneInTalk, Campaign as CampaignIcon, History, Assessment, Block, Settings,
  SettingsPhone, Call
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  aiCallApi, useAICallOverview, useAICallEvents, useInterval, formatBdt, formatNumber, ratio,
  dhaka, reasonText, canonicalMobile
} from "./useAICalling";
import {
  AICallingPage, SectionCard, Loading, ErrorState, SipStateChip, ConfirmDialog, KeyValue
} from "./AICallingComponents";

/* ------------------------------------------------------------------ test call */

const TEST_SCRIPTS = [
  { value: "", label: "Campaign agent (Settings prompt)" },
  { value: "signup", label: "New-signup sales call" },
  { value: "signup_followup", label: "New-signup follow-up (with ৳ offer)" },
  { value: "notice", label: "Purchase notice (one line)" },
];

const TestCallDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [script, setScript] = useState("");
  const [step, setStep] = useState("enter"); // enter | confirm | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [callId, setCallId] = useState("");

  useEffect(() => {
    if (open) {
      setMobile("");
      setScript("");
      setStep("enter");
      setError("");
      setCallId("");
    }
  }, [open]);

  const number = canonicalMobile(mobile);

  const next = () => {
    if (!number) return setError("Enter a Bangladeshi mobile number (01XXXXXXXXX)");
    setError("");
    setStep("confirm");
  };

  const call = async () => {
    setBusy(true);
    const res = await aiCallApi.testCall(number, script);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      setStep("enter");
      return;
    }
    toast.success(res.message || "Calling…");
    setCallId(res.data?.callId || "");
    setStep("done");
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Test call</DialogTitle>
      <DialogContent>
        {step === "enter" && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The AI agent calls this number now with a short test conversation. Use a staff phone. No member data is used and
              tools (payment link, callback, ticket) are only simulated.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Staff mobile"
              placeholder="01XXXXXXXXX"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && next()}
              inputProps={{ inputMode: "tel", maxLength: 20 }}
              error={!!error}
              helperText={error || "01XXXXXXXXX"}
            />
            <TextField select fullWidth label="Script" value={script} onChange={(e) => setScript(e.target.value)} sx={{ mt: 2 }}>
              {TEST_SCRIPTS.map((s) => (
                <MenuItem key={s.value || "agent"} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </>
        )}
        {step === "confirm" && (
          <Alert severity="warning">
            This places a real phone call to <b>{number}</b> now. Continue?
          </Alert>
        )}
        {step === "done" && (
          <Alert severity="success">
            Calling {number}…{callId ? ` (call ${callId.slice(-6)})` : ""} Watch it on the Live calls page.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {step === "enter" && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={next} disabled={!mobile.trim()}>Next</Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button onClick={() => setStep("enter")} disabled={busy}>Back</Button>
            <Button variant="contained" color="warning" onClick={call} disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Call />}>
              Call now
            </Button>
          </>
        )}
        {step === "done" && (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={() => navigate("/ai-calling/live")}>Open live calls</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ page */

const LINKS = [
  { to: "/ai-calling/live", label: "Live calls", text: "Calls in progress, live transcript, hang up", icon: PhoneInTalk },
  { to: "/ai-calling/campaigns", label: "Campaigns", text: "Who to call, launch, pause, results", icon: CampaignIcon },
  { to: "/ai-calling/calls", label: "Call history", text: "Every call with outcome, cost and transcript", icon: History },
  { to: "/ai-calling/reports", label: "Reports", text: "Funnel, profit, best hours and days", icon: Assessment },
  { to: "/ai-calling/dnc", label: "Do not call", text: "Numbers that are never called", icon: Block },
  { to: "/ai-calling/settings", label: "Settings", text: "Agent, voice, call windows, budget, rules", icon: Settings },
  { to: "/ai-calling/sip", label: "SIP line", text: "Phone line the agent calls from", icon: SettingsPhone },
];

const budgetColor = (p) => (p >= 100 ? "error" : p >= 80 ? "warning" : "primary");

const AICallingControl = () => {
  const navigate = useNavigate();
  const { overview, loading, error, fetch } = useAICallOverview();
  const [confirmOn, setConfirmOn] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [liveSip, setLiveSip] = useState(null);
  const refreshTimer = useRef({ id: null });

  useEffect(() => {
    fetch();
  }, [fetch]);
  useEffect(() => {
    setLiveSip(null); // a fresh overview replaces live SIP updates
  }, [overview]);
  useEffect(() => {
    const timer = refreshTimer.current;
    return () => clearTimeout(timer.id);
  }, []);
  useInterval(() => fetch({ quiet: true }), 30000);

  // live: SIP state right away, counts shortly after a call starts or ends
  const onEvent = useCallback(
    (type, data) => {
      if (type === "sip:status") setLiveSip(data);
      if (type === "call:update" || type === "call:ended") {
        clearTimeout(refreshTimer.current.id);
        refreshTimer.current.id = setTimeout(() => fetch({ quiet: true }), 2000);
      }
    },
    [fetch]
  );
  useAICallEvents(onEvent);

  const setEnabled = async (value) => {
    setSwitching(true);
    const res = await aiCallApi.saveSettings({ enabled: value });
    setSwitching(false);
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(value ? "AI calling is on" : "AI calling is off");
    if (res.body?.engineReloaded === false) toast.info("Saved. The calling process will pick it up when it runs.");
    await fetch({ quiet: true });
    return true;
  };

  const refreshButton = (
    <Button variant="outlined" startIcon={<Refresh />} onClick={() => fetch()} disabled={loading}>Refresh</Button>
  );

  if (loading && !overview) {
    return <AICallingPage title="AI Calling" subtitle="Phone calls made by the AI agent"><Loading /></AICallingPage>;
  }
  if (!overview) {
    return (
      <AICallingPage title="AI Calling" subtitle="Phone calls made by the AI agent" actions={refreshButton}>
        <ErrorState error={error || "Could not load AI calling"} onRetry={() => fetch()} />
      </AICallingPage>
    );
  }

  const { settings = {}, engine = {}, today = {}, secretKeySet, can = {} } = overview;
  const engineOk = !!engine.ok;
  const sip = { ...(engine.sip || {}), ...(liveSip || {}) };
  const savedSip = settings.sip || {};
  const sipConfigured = !!(savedSip.host && savedSip.username && savedSip.passwordSet);
  const sipState = engineOk ? sip.state : sipConfigured ? null : "unconfigured";
  const channels = engine.channels || { max: savedSip.maxChannels || 0, inUse: 0 };
  const channelPct = channels.max ? Math.min(100, (channels.inUse / channels.max) * 100) : 0;
  const budget = Number(today.budgetBdt) || 0;
  const spend = Number(today.spendBdt) || 0;
  const budgetPct = budget > 0 ? Math.min(100, (spend / budget) * 100) : 100;
  const network = engineOk ? engine.network : null;
  const activeCount = Array.isArray(engine.activeCalls) ? engine.activeCalls.length : channels.inUse || 0;
  const sipPending = engineOk && sip.version !== null && sip.version !== undefined && savedSip.version !== undefined && sip.version !== savedSip.version;
  const limits = settings.limits || {};
  const windows = (limits.callWindows || []).map((w) => `${w.start}–${w.end}`).join(", ");

  return (
    <AICallingPage
      title="AI Calling"
      subtitle="Phone calls made by the AI agent"
      actions={
        <>
          <Button variant="contained" startIcon={<Call />} onClick={() => setTestOpen(true)} disabled={!engineOk}>Test call</Button>
          {refreshButton}
        </>
      }
    >
      {/* Warnings */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
        {!secretKeySet && (
          <Alert severity="error">
            AICALL_SECRET_KEY isn't set on the server. The panel can't talk to the calling process and the SIP password can't be saved.
          </Alert>
        )}
        {secretKeySet && !engineOk && (
          <Alert severity="error">
            {engine.error === "engine_offline"
              ? "The calling process isn't running on the server, so no calls can be made."
              : `Can't reach the calling process: ${reasonText(engine.error) || "unknown error"}`}
          </Alert>
        )}
        {(!sipConfigured || sipState === "unconfigured") && (
          <Alert
            severity="warning"
            action={can.sipLine ? <Button color="inherit" size="small" onClick={() => navigate("/ai-calling/sip")}>Set up</Button> : undefined}
          >
            The SIP line isn't set up yet{can.sipLine ? "." : ". Ask a Super Admin to set it up."}
          </Alert>
        )}
        {sipConfigured && sipState === "failing" && (
          <Alert severity="error">The SIP line can't register{sip.lastError ? `: ${sip.lastError}` : "."}</Alert>
        )}
        {sipPending && (
          <Alert severity="info">SIP line changes are saved and will be applied when the calls in progress finish.</Alert>
        )}
        {!settings.enabled && (
          <Alert severity="info">AI calling is off. No member is called (test calls still work).</Alert>
        )}
        {settings.enabled && budget <= 0 && (
          <Alert severity="warning">The daily budget is ৳0, so no calls are made. Change it in Settings.</Alert>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Switch */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" className="aic-card">
            <CardContent>
              <Typography variant="body2" color="text.secondary">AI calling</Typography>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={!!settings.enabled}
                    disabled={switching}
                    onChange={(e) => (e.target.checked ? setConfirmOn(true) : setEnabled(false))}
                  />
                }
                label={<Typography variant="h6">{settings.enabled ? "On" : "Off"}</Typography>}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Off: no new calls. Calls in progress finish normally.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Engine */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" className="aic-card">
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">Calling process</Typography>
                <Chip size="small" color={engineOk ? "success" : "error"} label={engineOk ? "Online" : "Offline"} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                <Typography variant="body2">SIP line:</Typography>
                {sipState ? <SipStateChip state={sipState} /> : <Chip size="small" label="Unknown" />}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Last registered: {sip.lastRegisteredAt ? dhaka(sip.lastRegisteredAt) : "—"}
              </Typography>
              {network?.lines?.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2">
                    Phone line on: <b>{network.phone || "—"}</b>
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                    {network.lines.map((l) => {
                      const ok = l.reachesPbx !== false && l.reachesInternet !== false;
                      return (
                        <Chip
                          key={l.name}
                          size="small"
                          variant={l.name === network.phone ? "filled" : "outlined"}
                          color={ok ? "success" : "error"}
                          label={`${l.name}${l.share ? ` · AI ${l.share}%` : ""}${ok ? "" : " · down"}`}
                          title={`${l.ip} — PBX ${l.reachesPbx === false ? "unreachable" : "ok"}, internet ${l.reachesInternet === false ? "down" : "ok"}`}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {sip.lastError && (
                <Typography variant="caption" color="error" display="block" sx={{ wordBreak: "break-word" }}>
                  Last error: {sip.lastError}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Channels in use: <b>{formatNumber(channels.inUse)}</b> / {formatNumber(channels.max)}
              </Typography>
              <LinearProgress variant="determinate" value={channelPct} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
              {engineOk && savedSip.maxChannels > channels.max && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  The SIP line has {formatNumber(savedSip.maxChannels)} channels. "Calls at once" in Settings allows {formatNumber(channels.max)}.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" className="aic-card">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Today</Typography>
              <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Calls</Typography>
                  <Typography variant="h6">{formatNumber(today.calls)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Answered</Typography>
                  <Typography variant="h6">
                    {formatNumber(today.answered)}
                    {today.calls ? <Typography component="span" variant="caption" color="text.secondary"> ({ratio(today.answered, today.calls)})</Typography> : null}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Spent <b>{formatBdt(spend)}</b> of {formatBdt(budget)}
              </Typography>
              <LinearProgress variant="determinate" value={budgetPct} color={budgetColor(budgetPct)} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Daily budget (Bangladesh day). Test calls aren't counted here.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {activeCount > 0 && (
        <Alert
          severity="success"
          icon={<PhoneInTalk />}
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" onClick={() => navigate("/ai-calling/live")}>Watch</Button>}
        >
          {activeCount} call{activeCount === 1 ? "" : "s"} in progress
        </Alert>
      )}

      <SectionCard
        title="Current settings"
        action={<Button size="small" onClick={() => navigate("/ai-calling/settings")}>Change</Button>}
      >
        <Grid container spacing={2}>
          <KeyValue label="Agent">{settings.agent?.name}</KeyValue>
          <KeyValue label="Voice">{settings.agent?.voice}</KeyValue>
          <KeyValue label="Model">{settings.model}</KeyValue>
          <KeyValue label="Call windows (Bangladesh time)">{windows}</KeyValue>
          <KeyValue label="Longest call">{limits.maxCallSeconds ? `${limits.maxCallSeconds} s` : ""}</KeyValue>
          <KeyValue label="Calls at once">{limits.concurrency}</KeyValue>
          <KeyValue label="One call per member every">{limits.perUserDays ? `${limits.perUserDays} days` : ""}</KeyValue>
          <KeyValue label="Recordings">{settings.recordings?.enabled ? `On, kept ${settings.recordings.retentionDays} days` : "Off"}</KeyValue>
          <KeyValue label="Inbound support calls">{settings.inbound?.enabled ? "Answered by the agent" : "Off"}</KeyValue>
        </Grid>
        {settings.updatedAt && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Last changed {dhaka(settings.updatedAt)}{settings.updatedByName ? ` by ${settings.updatedByName}` : ""}
            </Typography>
          </>
        )}
      </SectionCard>

      <Typography variant="h6" sx={{ mb: 1.5 }}>Go to</Typography>
      <Grid container spacing={2}>
        {LINKS.map(({ to, label, text, icon: Icon }) => (
          <Grid item xs={12} sm={6} md={3} key={to}>
            <Card variant="outlined" className="aic-card">
              <CardActionArea onClick={() => navigate(to)} sx={{ height: "100%" }}>
                <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <Icon color="action" />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {label}
                      {to === "/ai-calling/live" && activeCount > 0 ? ` (${activeCount})` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{text}</Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ConfirmDialog
        open={confirmOn}
        title="Switch AI calling on?"
        confirmText="Switch on"
        confirmColor="warning"
        onClose={() => setConfirmOn(false)}
        onConfirm={() => setEnabled(true)}
      >
        <Typography gutterBottom>
          Members will get real AI phone calls from running campaigns and the channel ladder, only inside the call windows
          {windows ? ` (${windows}, Bangladesh time)` : ""} and up to the daily budget of {formatBdt(budget)}.
        </Typography>
        {!engineOk && <Alert severity="warning" sx={{ mt: 1 }}>The calling process isn't running, so nothing is called until it starts.</Alert>}
        {engineOk && sipState !== "registered" && <Alert severity="warning" sx={{ mt: 1 }}>The SIP line isn't registered, so calls can't go out yet.</Alert>}
      </ConfirmDialog>

      <TestCallDialog open={testOpen} onClose={() => setTestOpen(false)} />
    </AICallingPage>
  );
};

export default AICallingControl;
